import { Injectable, OnInit } from "@angular/core";
import { CorrectionModel, DataCorrectionInstance, DataCorrectionInstanceIndex, DataCorrectionInstanceIndexSearchSpec } from "../models/dataCorrection/dataCorrection.model";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { EntityMap, LunaEntity, PropertyMap } from "../builder/models/entity.model";
import { EntityBuilderService } from "../builder/services/entity-builder.service";
import { genId } from "../core/services/genId";
import { AuthService } from "./auth.service";
import { getPropertyValue } from "../common/models/data-manipulation-functions";


@Injectable()
export class DataCorrectionService implements OnInit {

	private _systemAssignedProperties: string[] = ['Identifier', 'Item Is Added', 'Item Is Deleted', 'Casing Plan Identifier'];

    constructor(public http: HttpClient, public authService: AuthService, public entityBuilderService: EntityBuilderService) { }

    ngOnInit() { }

    assignDataCorrectionInstance(id: string, userId: string | null): Observable<boolean> {
        return this.http.post<boolean>(`/api/data-correction/assign/${id}`, { userId });
    }

    getDataCorrectionInstance(id: string): Observable<DataCorrectionInstance> {
        return this.http.get<any>(`/api/data-correction/${id}`);
    }

    getDataCorrectionInstanceIndexes(spec: DataCorrectionInstanceIndexSearchSpec): Observable<DataCorrectionInstanceIndex[]> {
        return this.http.post<DataCorrectionInstanceIndex[]>(`/api/data-correction/instanceIndexes`, spec);
    }

    getObjectToCorrect(collectionName: string, partitionKey: string, id: string,): Observable<any> {
        return this.http.get<any>(`/api/data-correction/get-object-to-correct/${collectionName}/${partitionKey}/${id}`);
    }

    saveDataCorrectionInstance(instance: DataCorrectionInstance): Observable<any> {
        return this.http.post(`/api/data-correction/saveDataCorrectionInstance/`, instance);
    }
    
    generateCorrectionMap(instance: DataCorrectionInstance, entity: LunaEntity, object: any, count: number): CorrectionModel[] {
        const id = genId();
        let model = [] as CorrectionModel[];

        for (let i = 0; i < Object.keys(entity.schemaObject.properties).length; i++) {
			let parentPropertyName = Object.keys(entity.schemaObject.properties)[i];
            model.push(this.generatePropertyMap(instance, entity.schemaObject.properties, object, i));  
            
        }
        return model;
    }

    fixNameCasing(name: string): string {
        let result: string = "";
        let lastChar = name.substring(0, 1).toUpperCase();
        result = result.concat(lastChar);
        for (let s = 1; s < name.length; s++) {
            let nextChar = name.substring(s, s + 1);
            if (nextChar === nextChar.toUpperCase() && lastChar !== lastChar.toUpperCase() && nextChar !== ".") {
                result = result.concat(" ");
            }
            else if (lastChar === ".")
                nextChar = nextChar.toUpperCase();

            result = result.concat(nextChar);
            lastChar = nextChar;
        }

        //Now go through and replace all the periods
        while (result.includes(".")) {
            result = result.replace(".", " => ");
        }

        return result;
    }

    generatePropertyMap(instance: DataCorrectionInstance, propertyData: any, object: any, index: number, sourceDataPrefix: string = "", appendPropertyNameToPrefix: boolean = true): CorrectionModel {
        let builtCorrectionModel = { childCorrectionModels: [] } as CorrectionModel;
        var propertyName = Object.keys(propertyData)[index];

        if (appendPropertyNameToPrefix) {
            if (sourceDataPrefix != "")
                sourceDataPrefix += ".";

            sourceDataPrefix = sourceDataPrefix + propertyName;
        }

        let propertyType: string = undefined;
        const propertyDefinition = Object.values(propertyData)[index];

		propertyType = propertyDefinition["type"];
        builtCorrectionModel = this.setData(instance, propertyDefinition, object, propertyName, sourceDataPrefix);
		//console.log('correction model in propertymap', builtCorrectionModel);
        return builtCorrectionModel;
    } 

	setData(instance: DataCorrectionInstance, propertyDefinition: any, objectData: any, propertyName: string, sourceDataPrefix: string): CorrectionModel {
        let currModel = {} as CorrectionModel;

        if ((Object.keys(propertyDefinition)).some(key => key == "format")) {
            currModel.propertyFormat = propertyDefinition["format"];
        }

        if ((Object.keys(propertyDefinition)).some(key => key == "isOrganizationId")) {
            currModel.propertyIsOrganizationId = propertyDefinition["isOrganizationId"];
        }
        else
            currModel.propertyIsOrganizationId = false;

        if ((Object.keys(propertyDefinition)).some(key => key == "type")) {
            currModel.propertyType = propertyDefinition["type"];
        }        

        if ((Object.keys(propertyDefinition)).some(key => key == "displayName") && propertyDefinition["displayName"]) {
			currModel.propertyDisplayName = propertyDefinition["displayName"];			
        }
        else {
            currModel.propertyDisplayName = this.fixNameCasing(propertyName);
        }
		
        //Since most properties will be editable, it's more expedient to assume that if editability hasn't been set one way or the other, to make it editable
		if ((Object.keys(propertyDefinition)).some(key => key == "allowEdit")) {

			currModel.propertyAllowEdit = propertyDefinition["allowEdit"] == null ? true : propertyDefinition["allowEdit"];
		}
		else {
			currModel.propertyAllowEdit = true
		}          

		if (this._systemAssignedProperties.includes(currModel.propertyDisplayName)) {
			currModel.propertyDisplayName = `${currModel.propertyDisplayName} (System-Assigned)`;
			currModel.propertyAllowEdit = false;
		}
        //Use built path for datasource
        currModel.propertyDataSource = sourceDataPrefix;

        //Make the propertyId same as path, as that should always be unique
        currModel.propertyId = currModel.propertyDataSource;

        let objectDataValue = null;

		if (currModel.propertyDataSource) {
			objectDataValue = this.getData(currModel.propertyDataSource, currModel.propertyType, currModel.propertyFormat, objectData);
		}



        //Check if it's an array
        //If so we need to run through the children nad build their respective correction models
        if (currModel.propertyType === "array") {
            currModel.propertyValue = ""

            //IF it failed to find anything, set to null
            if (objectDataValue == "")
                objectDataValue = null;


            let childCorrectionModels = this.buildArrayCorrectionModel(instance, propertyDefinition, objectData, currModel);
			currModel.childCorrectionModels = childCorrectionModels;

			if (objectDataValue == null && objectData != null) {
				currModel.childCorrectionModels = [];
			}
            //Now do a second pass with no object data to get an array of one item with values to act as default for addition
            currModel.arrayChildCorrectionModelFormat = this.buildArrayCorrectionModel(instance, propertyDefinition, null, currModel)[0];
        }
        else if (currModel.propertyType === "object") {

            let objectProperties = propertyDefinition["properties"];

            let childCorrectionModels = [];
            for (let x = 0; x < Object.keys(objectProperties).length; x++) {
                let parentPropertyName = Object.keys(objectProperties)[x];
                childCorrectionModels.push(this.generatePropertyMap(instance, objectProperties, objectData, x, currModel.propertyDataSource, true));

            }
            currModel.childCorrectionModels = childCorrectionModels;
        }
        else {
            if (currModel.propertyDataSource)
                currModel.propertyValue = objectDataValue;
		}
	
        return currModel;          
    }

    buildArrayCorrectionModel(instance: DataCorrectionInstance, itemDefinition: any, objectData: any, parentCorrectionModel: CorrectionModel): CorrectionModel[] {
        let arrayElementCorrectionModels: CorrectionModel[] = [];

        //This is an array, we're going to need to figure out how many items exist in actual object, and iterate through them generating all the object fields
        //for each item
        let actualItemCount = 1;
        if (objectData !== null && objectData !== undefined) {
            //try to fetch the actual array
            let dataArray = this.getData(parentCorrectionModel.propertyDataSource, "array", "", objectData);
            if (dataArray !== null && dataArray !== undefined && dataArray !== "")
                actualItemCount = dataArray.length;
        }

        //Any property of type array should also have an items list
        if (Object.keys(itemDefinition).some(a => a === "items")) {
            let itemsDefinition = itemDefinition["items"];

            //Check the type of item in the array
            let arrayType = itemsDefinition["type"];

            for (let x = 0; x < actualItemCount; x++) {
                let correctionModel: CorrectionModel;
                //Pass in the current prefix, and append new source
                let tempSourceDataPrefix = parentCorrectionModel.propertyDataSource + "[" + x + "]";

                switch (arrayType) {
                    case "array": {

                        //TODO: This might not work right yet, as we're missing intermediate steps
                        //correctionModel.childCorrectionModels = this.buildArrayCorrectionModel(itemsDefinition, actualItemArray )
                    }
                    case "object": {
                    }
					default: {
						let propertyJsonString = `{"${parentCorrectionModel.propertyDataSource}": { "displayName": "${parentCorrectionModel.propertyDisplayName} ${x + 1}", "type": "${itemsDefinition["type"]}" }}`;

                        let jsonObject = JSON.parse(propertyJsonString);

						if (Object.keys(itemsDefinition).some(a => a === "properties")) {
							jsonObject[parentCorrectionModel.propertyDataSource]["properties"] = itemsDefinition["properties"];
							jsonObject[parentCorrectionModel.propertyDataSource]["properties"]["itemIsAdded"] = { type: 'boolean' };
							jsonObject[parentCorrectionModel.propertyDataSource]["properties"]["itemIsDeleted"] = { type: 'boolean' };
						} 

						correctionModel = this.generatePropertyMap(instance, jsonObject, objectData, 0, tempSourceDataPrefix, false);					
						

						if (correctionModel.childCorrectionModels != null && correctionModel.childCorrectionModels.length > 0) {
							correctionModel.childCorrectionModels.forEach(c => {
								if (this._systemAssignedProperties.includes(c.propertyDisplayName)) {
									c.propertyDisplayName = `${c.propertyDisplayName} (System-Assigned)`;
									c.propertyAllowEdit = false;
								}
							});
						}
						arrayElementCorrectionModels.push(correctionModel);
                    }
                }
            }
        }

        return arrayElementCorrectionModels;
    }


    getData(source: string, type: string, format: string, currentObject: any): any {

        switch (type) {
            case "string": {
                switch (format) {
                    case "date-time":
                    case "date": {
                        let stringDate = getPropertyValue(currentObject, source, "");
                        if (stringDate) {
                            let date = new Date(stringDate);
                            return date.toISOString().substring(0, 10);
                        }
                        else return stringDate;
                    }
                    default: {
                        return getPropertyValue(currentObject, source, "");
                    }
                }
            }
            case "integer":
            case "number":
            case "boolean": 
            default: {
                return getPropertyValue(currentObject, source, "");
            }
        }         
    }
}
   
